// src/Chat/AsistenteVirtual.js
import React, { useState, useEffect, useRef } from 'react';
import { useChat } from './ChatContext';
import {
    MainContainer,
    ChatContainer,
    MessageList,
    Message,
    MessageInput,
    Avatar,
    TypingIndicator,
    Button as ChatUIButton
} from '@chatscope/chat-ui-kit-react';
import './AsistenteVirtual.css';
import botAvatar from './bot-avatar.png';

const ChatIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="28px" height="28px">
        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
    </svg>
);

const ArrowDownIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24px" height="24px">
        <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
    </svg>
);

export const AsistenteVirtual = () => {
    const { messages, sendMessage, sendButtonPress, isConnected, isThinking, sendFile, startChat, hasChatToken } = useChat();
    const [isOpen, setIsOpen] = useState(Boolean(hasChatToken));
    const fileInputRef = useRef(null);
    const [showScrollButton, setShowScrollButton] = useState(false);
    
    const widgetRef = useRef(null);
    const prevMessagesCount = useRef(messages.length);

    useEffect(() => {
        if (hasChatToken) {
            setIsOpen(true);
            startChat();
        }
    }, [hasChatToken]);

    const handleOpen = () => {
        setIsOpen(true);
        startChat();
    };

    const getScrollableNode = () => {
        if (widgetRef.current) {
            return widgetRef.current.querySelector('.cs-message-list__scroll-wrapper');
        }
        return null;
    };

    const scrollToBottom = () => {
        const scrollableNode = getScrollableNode();
        if (scrollableNode) {
            scrollableNode.scrollTo({
                top: scrollableNode.scrollHeight,
                behavior: 'auto'
            });
        }
    };

    useEffect(() => {
        const scrollableNode = getScrollableNode();
        
        if (scrollableNode) {
            const handleScroll = () => {
                const { scrollTop, scrollHeight, clientHeight } = scrollableNode;
                const isScrolledUp = scrollHeight - scrollTop > clientHeight + 150;
                setShowScrollButton(isScrolledUp);
            };

            scrollableNode.addEventListener('scroll', handleScroll);
            return () => scrollableNode.removeEventListener('scroll', handleScroll);
        }
    }, [isOpen, messages.length]);

    useEffect(() => {
        const scrollableNode = getScrollableNode();
        if (scrollableNode) {
            const newMessagesCount = messages.length - prevMessagesCount.current;

            if (newMessagesCount > 0) {
                const lastUserMessageIndex = messages.map(m => m.type).lastIndexOf('sent');
                const firstNewBotMessageIndex = lastUserMessageIndex !== -1 ? lastUserMessageIndex + 1 : 0;

                if (messages[firstNewBotMessageIndex]?.type === 'received') {
                    const messageElements = scrollableNode.querySelectorAll('.cs-message');
                    const firstNewMessageElement = messageElements[firstNewBotMessageIndex];
                    
                    if (firstNewMessageElement) {
                        firstNewMessageElement.scrollIntoView({ behavior: 'auto', block: 'center' });
                        setShowScrollButton(true);
                    }
                } else {
                    scrollToBottom();
                }
            }
            prevMessagesCount.current = messages.length;
        }
    }, [messages]);

    const handleSend = (innerHtml, textContent, innerText) => {
        const text = (innerText || textContent || innerHtml || '').replace(/&nbsp;/gi, ' ').replace(/\u00a0/g, ' ').trim();
        if (text) {
            sendMessage(text);
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            sendFile(file);
        }
        event.target.value = null; 
    };

    const formatText = (text) => {
        if (!text) return '';
        return text
            .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
            .replace(/\n/g, '<br />');
    };

    const CustomBotMessage = ({ messageId, payload }) => {
        const safePayload = payload || {};
        const { header, body, text, options, buttonText, url, buttonsDisabled, fileName, fileB64, mimeType } = safePayload;
        const messageText = body || text || '';

        const handleDownload = () => {
            const byteCharacters = atob(fileB64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: mimeType });

            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        };

        return (
            <div className="custom-message-container">
                {header && <div className="custom-message-header">{header}</div>}
                <div className="custom-message-body" dangerouslySetInnerHTML={{ __html: formatText(messageText) }} />
                
                {(payload.type === 'options' && options) && (
                    <div className="custom-message-footer">
                        {Object.entries(options).map(([payloadId, title]) => (
                            <ChatUIButton 
                                key={payloadId} 
                                onClick={() => sendButtonPress(messageId, payloadId, title)}
                                border
                                disabled={buttonsDisabled} 
                            >
                                {title}
                            </ChatUIButton>
                        ))}
                    </div>
                )}
                {payload.type === 'cta' && url && (
                    <div className="custom-message-footer">
                        <ChatUIButton onClick={() => window.open(url, '_blank')} border>
                            {buttonText}
                        </ChatUIButton>
                    </div>
                )}
                {payload.type === 'document' && fileB64 && (
                    <div className="custom-message-footer">
                        <ChatUIButton onClick={handleDownload} border>
                            Descargar {fileName}
                        </ChatUIButton>
                    </div>
                )}
            </div>
        );
    };

    if (!isOpen) {
        return (
            <button className="chat-fab" onClick={handleOpen}>
                <ChatIcon />
            </button>
        );
    }
    
    return (
        <div className="chat-widget-container" ref={widgetRef}>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
            />
            
            <div className="chat-header titulo-modal">
                <h5>Asistente Virtual</h5>
                <span className="status-indicator">{isConnected ? "En línea" : "Conectando..."}</span>
                <button className="chat-close-button" onClick={() => setIsOpen(false)}>&times;</button>
            </div>
            <MainContainer>
                <ChatContainer>
                    <MessageList 
                        autoScrollToBottom={false}
                        typingIndicator={isThinking ? <TypingIndicator content="Asistente está escribiendo..." /> : null}
                    >
                        {messages.map((msg) => {
                            const isBot = msg.type === 'received';
                            const dateObj = msg.timestamp ? new Date(msg.timestamp) : null;
                            const isValidDate = dateObj && !isNaN(dateObj.getTime());
                            const sentTime = isValidDate ? dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                            const channelBadge = msg.source === 'whatsapp' ? ' · WhatsApp' : '';
                            const footerText = sentTime ? `${sentTime}${channelBadge}` : (channelBadge ? 'WhatsApp' : '');

                            return (
                                <Message
                                    key={msg.id}
                                    model={{
                                        direction: isBot ? 'incoming' : 'outgoing',
                                        position: "single"
                                    }}
                                >
                                    <Message.CustomContent>
                                        {isBot ? <CustomBotMessage messageId={msg.id} payload={msg.payload} /> : (msg.payload?.text || '')}
                                    </Message.CustomContent>
                                    {footerText && <Message.Footer sentTime={footerText} />}
                                </Message>
                            );
                        })}
                    </MessageList>
                    <MessageInput 
                        placeholder={isConnected ? "Escribe tu mensaje aquí..." : "Conectando..."} 
                        onSend={handleSend}
                        attachButton={true}
                        // Se añade optional chaining (?.) como medida de seguridad
                        onAttachClick={() => fileInputRef.current?.click()}
                        disabled={!isConnected || isThinking}
                    />
                </ChatContainer>
            </MainContainer>
            {showScrollButton && (
                <button className="scroll-to-bottom-button" onClick={() => scrollToBottom()}>
                    <ArrowDownIcon />
                </button>
            )}
        </div>
    );
};